import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { InputField } from "@/components/InputField/InputField";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, toastFailure, useSearch } from "@/utils";

import {
	useCreateLiPeopleMutation,
	useUpdateLiPeopleMutation,
} from "@/pages/Private/redux/liPeople/liPeople.api";

import { LiPeopleType } from "@/enum/li-people.enum";
import { classNames } from "@/utils/classNames";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { useGetProfilesQuery } from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";

import { Icon } from "@/components/Icon/Icon";

import { resolveSourceMode } from "../../List/pages/ListCreate/utils/sourceMode";

import { List } from "../../List/schema/list";

import { LinkedinParams, LinkedinTagAutocomplete } from "./LinkedinTagAutocomplete";
import { CreateLiPeople, CreateLiPeopleSchema, LiPeople } from "../schema/liPeople";
import { LinkedinCompanyTagAutocomplete } from "./LinkedinCompanyTagAutocomplete";
import { LinkedinPersonTagAutocomplete } from "./LinkedinPersonTagAutocomplete";
import { buildLiPeoplePayload } from "./buildLiPeoplePayload";

interface CreateLiPeopleFormProps {
	liPeople?: LiPeople | null;
	list: List;
	handleSelect?: (job: LiPeople | null) => void;
	handleDuplicate?: (job: LiPeople | null) => void;
}

export const CreateLiPeopleForm: FunctionComponent<CreateLiPeopleFormProps> = ({
	liPeople,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPeople.${key}`);
	const [createLiPeople, { isLoading, error }] = useCreateLiPeopleMutation();
	const [updateLiPeople, { isLoading: updateIsLoading, error: updateError }] =
		useUpdateLiPeopleMutation();

	const [additionalColumns, setAdditionalColumns] = useState<boolean>(false);

	const { query: profileQuery, setSearchValue: setProfileSearchValue } = useSearch();

	const { data: profiles } = useGetProfilesQuery({
		page: 1,
		limit: 20,
		searchValue: profileQuery,
	});

	const [schools, setSchools] = useState<LinkedinParams[]>();
	const [currentCompaniesList, setCurrentCompaniesList] = useState<LinkedinParams[]>();
	const [pastCompaniesList, setPreviousCompaniesList] = useState<LinkedinParams[]>();
	const [followerOf, setFollowerOf] = useState<LinkedinParams | null>();
	const [connectionsOf, setConnectionsOf] = useState<LinkedinParams | null>();
	const [locations, setLocations] = useState<LinkedinParams[]>();
	const [languages, setLanguages] = useState<LinkedinParams[]>();
	const [serviceCategories, setServiceCategories] = useState<LinkedinParams[]>();
	const [industries, setIndustries] = useState<LinkedinParams[]>();

	const initialState = {
		name: "",
		urls: "",
		type: resolveSourceMode(Object.values(LiPeopleType), LiPeopleType.SPECIFIC),
		firstConnection: false,
		secondConnection: false,
		thirdConnection: false,
		plannedScrapingUserId: null,
	};

	const {
		reset,
		handleSubmit,
		setValue,
		formState: { errors },
		watch,
	} = useForm<CreateLiPeople>({
		defaultValues: initialState,
		resolver: zodResolver(CreateLiPeopleSchema),
	});

	const currentFormState = watch();

	useEffect(() => {
		if (liPeople) {
			const arr = [
				{ name: "id" as keyof CreateLiPeople, value: liPeople?.id },
				{ name: "type" as keyof CreateLiPeople, value: liPeople?.type },
				{ name: "name" as keyof CreateLiPeople, value: liPeople?.name },
				{ name: "urls" as keyof CreateLiPeople, value: liPeople.liPeopleEntries?.[0].url || "" },
				{
					name: "firstConnection" as keyof CreateLiPeople,
					value: liPeople.firstConnection || false,
				},
				{
					name: "secondConnection" as keyof CreateLiPeople,
					value: liPeople.secondConnection || false,
				},
				{
					name: "thirdConnection" as keyof CreateLiPeople,
					value: liPeople.thirdConnection || false,
				},
				{ name: "locationList" as keyof CreateLiPeople, value: liPeople.locationList },
				{ name: "schoolList" as keyof CreateLiPeople, value: liPeople.schoolList },
				{ name: "industryList" as keyof CreateLiPeople, value: liPeople.industryList },
				{
					name: "serviceCategoryList" as keyof CreateLiPeople,
					value: liPeople.serviceCategoryList,
				},
				{ name: "languageList" as keyof CreateLiPeople, value: liPeople.languageList },
				{ name: "keyword" as keyof CreateLiPeople, value: liPeople.keyword },
				{ name: "titleFreeText" as keyof CreateLiPeople, value: liPeople.titleFreeText },
				{ name: "firstName" as keyof CreateLiPeople, value: liPeople.firstName },
				{ name: "lastName" as keyof CreateLiPeople, value: liPeople.lastName },
				{ name: "companyFreeText" as keyof CreateLiPeople, value: liPeople.companyFreeText },
				{ name: "schoolFreeText" as keyof CreateLiPeople, value: liPeople.schoolFreeText },
				{
					name: "plannedScrapingUserId" as keyof CreateLiPeople,
					value: liPeople.plannedScrapingUserId,
				},
				{
					name: "currentCompaniesList" as keyof CreateLiPeople,
					value: liPeople.currentCompaniesList?.map((company) => ({
						urn: company.urn,
						title: company.title,
						image: company.image,
						subtitle: company.subtitle,
					})),
				},
				{
					name: "pastCompaniesList" as keyof CreateLiPeople,
					value: liPeople.pastCompaniesList?.map((company) => ({
						urn: company.urn,
						title: company.title,
						image: company.image,
						subtitle: company.subtitle,
					})),
				},
				{
					name: "followerOf" as keyof CreateLiPeople,
					value: liPeople.followerOf?.urn
						? {
								urn: liPeople.followerOf?.urn || "",
								title: liPeople.followerOf?.title || "",
								image: liPeople.followerOf?.image || "",
								subtitle: liPeople.followerOf?.subtitle || "",
							}
						: null,
				},
				{
					name: "connectionsOf" as keyof CreateLiPeople,
					value: liPeople.connectionsOf?.urn
						? {
								urn: liPeople.connectionsOf?.urn || "",
								title: liPeople.connectionsOf?.title || "",
								image: liPeople.connectionsOf?.image || "",
								subtitle: liPeople.connectionsOf?.subtitle || "",
							}
						: null,
				},
			];

			handleSaveFormStateMultiple(arr);

			setLocations(
				(liPeople.locationList?.map((l) => {
					return { name: l.title, id: l.urn };
				}) as unknown as LinkedinParams[]) || []
			);
			setSchools(
				(liPeople.schoolList?.map((l) => {
					return { name: l.title, id: l.urn };
				}) as unknown as LinkedinParams[]) || []
			);
			setIndustries(
				(liPeople.industryList?.map((l) => {
					return { name: l.title, id: l.urn };
				}) as unknown as LinkedinParams[]) || []
			);
			setServiceCategories(
				(liPeople.serviceCategoryList?.map((l) => {
					return { name: l.title, id: l.urn };
				}) as unknown as LinkedinParams[]) || []
			);
			setLanguages(
				(liPeople.languageList?.map((l) => {
					return { name: l.title, id: l.urn };
				}) as unknown as LinkedinParams[]) || []
			);

			setCurrentCompaniesList(
				(liPeople.currentCompaniesList?.map((l) => {
					return { name: l.title, id: l.urn, image: l.image, subtitle: l.subtitle };
				}) as unknown as LinkedinParams[]) || []
			);
			setPreviousCompaniesList(
				(liPeople.pastCompaniesList?.map((l) => {
					return { name: l.title, id: l.urn, image: l.image, subtitle: l.subtitle };
				}) as unknown as LinkedinParams[]) || []
			);

			setConnectionsOf(
				liPeople.connectionsOf?.urn
					? {
							id: liPeople.connectionsOf?.urn || "",
							name: liPeople.connectionsOf?.title || "",
							image: liPeople.connectionsOf?.image || "",
							subtitle: liPeople.connectionsOf?.subtitle || "",
						}
					: null
			);

			setFollowerOf(
				liPeople.followerOf?.urn
					? {
							id: liPeople.followerOf?.urn || "",
							name: liPeople.followerOf?.title || "",
							image: liPeople.followerOf?.image || "",
							subtitle: liPeople.followerOf?.subtitle || "",
						}
					: null
			);
			if (
				liPeople.schoolList?.length ||
				liPeople.locationList?.length ||
				liPeople.industryList?.length ||
				liPeople.serviceCategoryList?.length ||
				liPeople.languageList?.length ||
				liPeople.followerOf?.urn ||
				liPeople.currentCompaniesList?.length ||
				liPeople.pastCompaniesList?.length ||
				liPeople.connectionsOf?.urn
			) {
				setAdditionalColumns(true);
			}
		} else {
			reset();
		}
	}, [liPeople, reset]);

	const handleSaveFormStateMultiple = async (
		values: {
			value: CreateLiPeople[keyof CreateLiPeople];
			name: keyof CreateLiPeople;
		}[]
	) => {
		const newState = values.reduce(
			(acc, value) => {
				return {
					...acc,
					[value.name]: value.value,
				};
			},
			{ ...currentFormState }
		);

		reset(newState);
	};

	const onSubmit = async (values: CreateLiPeople) => {
		try {
			const newValue = buildLiPeoplePayload(values, {
				schools,
				locations,
				industries,
				serviceCategories,
				languages,
				currentCompaniesList,
				pastCompaniesList,
				followerOf,
				connectionsOf,
			});

			if (values.id && !handleDuplicate) {
				await updateLiPeople({
					id: values.id,
					...newValue,
					listId: list.id,
				}).unwrap();
			} else {
				await createLiPeople({ ...newValue, listId: list.id }).unwrap();
			}

			handleSelect?.(null);
			handleDuplicate?.(null);

			reset(initialState);
		} catch (err) {
			toastFailure(err, t("errors.saveFailed"));
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];
	const buttonTitle =
		handleDuplicate && liPeople?.id
			? t("basics.copyCriteria")
			: liPeople?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<>
			<div className="p-4 mt-8 mb-8 rounded-xl border border-gray-200 bg-brand-white">
				<div className="text-smd text-text font-bold mb-5">Add a source</div>
				<div className="flex flex-row w-full mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setValue("name", event.target.value);
						}}
						label={"Source name"}
						name={"name"}
						placeholder={ts("namePlaceholder")}
						value={currentFormState?.name || ""}
					/>
				</div>

				{currentFormState.type === LiPeopleType.SPECIFIC && (
					<div className="flex flex-row w-full mb-1">
						<InputField
							error={!!errors.urls?.message}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								setValue("urls", event.target.value);
							}}
							label={ts("urls")}
							name={"urls"}
							placeholder={ts("urlsPlaceholder")}
							showError={!!errors.urls?.message}
							value={currentFormState?.urls || ""}
						/>
					</div>
				)}

				<div className="flex flex-row w-full mb-4">
					<AutoComplete
						data={
							profiles?.data?.map((c) => {
								return {
									id: c.id,
									title: c.firstName + " " + c.lastName,
								} as AutoCompleteItem;
							}) || []
						}
						handleSearch={(value: string) => setProfileSearchValue(value)}
						handleSelect={async (item?: AutoCompleteItem) => {
							const newValue = (item?.id as number) ?? null;

							await setValue("plannedScrapingUserId", newValue);
						}}
						information="Select a user to scrape the people search results. Leave empty for random profile. The user needs to be logged in with linkedin. The results will be better, when the user has linkedin premium."
						label={"Select scraping signalist user"}
						placeholder="Leave empty for random profile"
						selectedItem={
							currentFormState.plannedScrapingUserId
								? profiles?.data.find((p) => p.id === currentFormState.plannedScrapingUserId)
										?.firstName +
									" " +
									profiles?.data.find((p) => p.id === currentFormState.plannedScrapingUserId)
										?.lastName
								: ""
						}
					/>
				</div>

				{currentFormState.type === LiPeopleType.SEARCH && (
					<>
						<div className="flex flex-col w-full mb-6">
							<div className="text-sm font-medium text-gray-900 mb-2">Your Linkedin connection</div>
							<div className="flex flex-row gap-4">
								<div className="flex flex-row">
									<div
										className={classNames(
											"cursor-pointer border border-gray-200 border-r-0 text-placeholder rounded-l-xl px-3 py-3",
											currentFormState?.firstConnection ? "bg-brand-light text-white" : "bg-white"
										)}
										onClick={() => setValue("firstConnection", !currentFormState?.firstConnection)}
									>
										1st
									</div>
									<div
										className={classNames(
											"cursor-pointer border border-gray-200 border-r-0 text-placeholder px-2 py-3",
											currentFormState?.secondConnection ? "bg-brand-light text-white" : "bg-white"
										)}
										onClick={() =>
											setValue("secondConnection", !currentFormState?.secondConnection)
										}
									>
										2nd
									</div>
									<div
										className={classNames(
											"cursor-pointer border border-gray-200 text-placeholder rounded-r-xl px-3 py-3",
											currentFormState?.thirdConnection ? "bg-brand-light text-white" : "bg-white"
										)}
										onClick={() => setValue("thirdConnection", !currentFormState?.thirdConnection)}
									>
										3rd
									</div>
								</div>
								<div
									className={classNames(
										"cursor-pointer border border-gray-200 text-placeholder rounded-xl px-6 py-3",
										currentFormState?.firstConnection &&
											currentFormState?.secondConnection &&
											currentFormState?.thirdConnection
											? "bg-brand-light text-white"
											: "bg-white"
									)}
									onClick={() => {
										const allActive =
											currentFormState?.firstConnection &&
											currentFormState?.secondConnection &&
											currentFormState?.thirdConnection;

										if (allActive) {
											handleSaveFormStateMultiple([
												{
													value: false,
													name: "firstConnection",
												},
												{
													value: false,
													name: "secondConnection",
												},
												{
													value: false,
													name: "thirdConnection",
												},
											]);
										} else {
											handleSaveFormStateMultiple([
												{
													value: true,
													name: "firstConnection",
												},
												{
													value: true,
													name: "secondConnection",
												},
												{
													value: true,
													name: "thirdConnection",
												},
											]);
										}
									}}
								>
									All
								</div>
							</div>
						</div>
						<div className="text-smd mb-2 mt-2">Search Criteria</div>
						<div className="flex flex-row w-full mb-1">
							<InputField
								error={!!errors.keyword?.message}
								handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
									setValue("keyword", event.target.value);
								}}
								label={ts("keyword")}
								name={"keyword"}
								placeholder={ts("keywordPlaceholder")}
								value={currentFormState?.keyword || ""}
							/>
						</div>
						<div className="flex flex-row w-full mb-1">
							<InputField
								error={!!errors.titleFreeText?.message}
								handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
									setValue("titleFreeText", event.target.value);
								}}
								label={ts("titleFreeText")}
								name={"titleFreeText"}
								placeholder={ts("titleFreeTextPlaceholder")}
								value={currentFormState?.titleFreeText || ""}
							/>
						</div>

						<div className="flex flex-row w-full mb-1">
							<InputField
								error={!!errors.companyFreeText?.message}
								handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
									setValue("companyFreeText", event.target.value);
								}}
								label={ts("companyFreeText")}
								name={"companyFreeText"}
								placeholder={ts("companyFreeTextPlaceholder")}
								value={currentFormState?.companyFreeText || ""}
							/>
						</div>
					</>
				)}

				<div className="flex flex-col gap-4 w-full mb-4">
					{getAllErrors(error || updateError, formErrors).length
						? renderErrorMessages(getAllErrors(error || updateError, formErrors))
						: null}
				</div>
			</div>
			{currentFormState.type === LiPeopleType.SEARCH && (
				<div
					className={classNames(
						"p-4 mb-8 w-full items-start rounded-xl border border-gray-200",
						"flex flex-col text-left",
						additionalColumns ? "bg-brand-white" : "bg-white"
					)}
				>
					<button
						className={classNames(
							"w-full items-center justify-between group cursor-pointer flex flex-row text-left gap-6   mb-5"
						)}
						onClick={() => {
							setAdditionalColumns(!additionalColumns);
						}}
					>
						<div className="flex flex-col">
							<div className="text-smd text-text font-bold">Additional filter criteria</div>
							<div className="text-ssm text-text-meta">
								Filter by even more criteria like Languages, <br />
								Industries, Service Categories, etc.
							</div>
						</div>
						<div
							className={classNames(
								"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
								additionalColumns
									? "border-border-light bg-white"
									: "border-transparent bg-background-light"
							)}
						>
							<Icon
								className="text-text-meta group-hover:text-white !w-[12px] h-auto"
								icon={additionalColumns ? faChevronUp : faChevronDown}
							/>
						</div>
					</button>

					{additionalColumns && (
						<>
							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinCompanyTagAutocomplete
									handleValueChange={(value: LinkedinParams[]) => {
										setCurrentCompaniesList(value);
									}}
									placeholder="e.g. Signalist"
									type={"currentCompany"}
									value={currentCompaniesList}
								/>
							</div>
							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinCompanyTagAutocomplete
									handleValueChange={(value: LinkedinParams[]) => {
										setPreviousCompaniesList(value);
									}}
									placeholder="e.g. Signalist"
									type={"previousCompany"}
									value={pastCompaniesList}
								/>
							</div>

							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinPersonTagAutocomplete
									handleValueChange={(value: LinkedinParams | null) => {
										setFollowerOf(value);
									}}
									placeholder="e.g. Signalist"
									type={"followerOf"}
									value={followerOf || undefined}
								/>
							</div>

							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinPersonTagAutocomplete
									handleValueChange={(value: LinkedinParams | null) => {
										setConnectionsOf(value);
									}}
									placeholder="e.g. Signalist"
									type={"connectionsOf"}
									value={connectionsOf || undefined}
								/>
							</div>

							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinTagAutocomplete
									handleValueChange={(value: LinkedinParams[]) => {
										setSchools(value);
									}}
									placeholder="e.g. University of Berlin"
									type={"school"}
									value={schools}
								/>
							</div>
							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinTagAutocomplete
									handleValueChange={(value: LinkedinParams[]) => {
										setLocations(value);
									}}
									placeholder="e.g. Berlin, Germany"
									type={"location"}
									value={locations}
								/>
							</div>
							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinTagAutocomplete
									handleValueChange={(value: LinkedinParams[]) => {
										setIndustries(value);
									}}
									placeholder="e.g. Marketing"
									type={"industry"}
									value={industries}
								/>
							</div>
							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinTagAutocomplete
									handleValueChange={(value: LinkedinParams[]) => {
										setServiceCategories(value);
									}}
									placeholder="e.g. Marketing"
									type={"serviceCategory"}
									value={serviceCategories}
								/>
							</div>
							<div className="flex flex-row gap-4 w-full mb-4">
								<LinkedinTagAutocomplete
									handleValueChange={(value: LinkedinParams[]) => {
										setLanguages(value);
									}}
									placeholder="e.g. English"
									type={"language"}
									value={languages}
								/>
							</div>
						</>
					)}
				</div>
			)}
			<div className="flex flex-row gap-4 w-full justify-end">
				<div className="flex gap-4 w-full justify-end">
					{handleSelect && (
						<div className="flex max-w-[100px] w-full">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								title="cancel"
								onClick={() => {
									reset(initialState);
									setLocations([]);
									setSchools([]);
									setIndustries([]);
									setServiceCategories([]);
									setLanguages([]);
									setConnectionsOf(null);
									setFollowerOf(null);
									setCurrentCompaniesList([]);
									setPreviousCompaniesList([]);
									handleSelect?.(null);
									handleDuplicate?.(null);
								}}
							/>
						</div>
					)}
				</div>
				<div className="flex max-w-[150px] w-full">
					{currentFormState.type === LiPeopleType.MANUAL && (
						<div className="text-sm text-gray-500">
							Manual sources will be created automatically
						</div>
					)}
					{currentFormState.type !== LiPeopleType.MANUAL && (
						<Button
							isLoading={isLoading || updateIsLoading}
							title={buttonTitle}
							onClick={handleSubmit(onSubmit)}
						/>
					)}
				</div>
			</div>
		</>
	);
};
